import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  MdAdd,
  MdCategory,
  MdCheck,
  MdClose,
  MdDelete,
  MdEditNote,
  MdLocalFlorist,
  MdPalette,
  MdPayments,
  MdPrint,
  MdRefresh,
} from "react-icons/md";

import axios from "../axiosConfig";
import { BASE_SERVER_URL } from "../sampleData";
import { useAuth } from "../auth/AuthContext";
import { can } from "../auth/permissions";
import { BrandingSection } from "./settingsBranding";
import "../settings-redesign.css";

/* ------------------------------------------------------------------ *
 * Settings (redesign) — /settings
 *
 * Replaces src/pages/setupPage.js (kept at /settings-v1) and the
 * hardcoded mock that used to sit at /settings-v2. Everything on this
 * page is wired to the endpoints the legacy screen used:
 *
 *   GET  /settings/getAll        -> response[0]: gst, delivery_fee,
 *                                   sash_prices (comma strings),
 *                                   product_types, cc_types,
 *                                   pedestal_types, easel_types (JSON
 *                                   string arrays)
 *   POST /settings/update        -> a partial settings object
 *   GET  /api/flowers/get-all
 *   POST /api/flowers/add-new
 *   PUT  /api/flowers/update/:id
 *   DEL  /api/flowers/delete/:id
 *   GET  /api/printers, GET/POST /api/printers/defaults
 *
 * What changes:
 *   - three bootstrap columns of "golden-card" boxes, all on screen at
 *     once                              -> four sections behind a nav
 *   - to file a product type under
 *     Casket cover / Pedestal / Easel
 *     you flipped that card into edit
 *     mode, which dimmed the page, then
 *     clicked types in another card     -> the three categories are
 *                                          toggles on the type itself
 *   - deleting a product type left it
 *     behind in every category list it
 *     was in                            -> the categories are cleaned up
 *                                          in the same update
 *   - delivery fees and sash prices
 *     were one comma-separated string
 *     you typed by hand, rejected in
 *     red when it didn't parse          -> one chip per price
 *   - deleting anything happened on a
 *     bare icon click                   -> an inline confirm in the row
 *   - `canManage` only guarded
 *     /settings/update, so a staff user
 *     still saw every Add button and
 *     every × (flowers ignored it
 *     entirely and would really delete) -> read-only roles get a banner
 *                                          and disabled controls
 *
 * settings:read reaches this page (App.js gates the route);
 * settings:manage is what allows a change. The server re-checks both.
 * ------------------------------------------------------------------ */

const SECTIONS = [
  { id: "fees", label: "Fees & prices", icon: MdPayments },
  { id: "types", label: "Product types", icon: MdCategory },
  { id: "flowers", label: "Content flowers", icon: MdLocalFlorist },
  { id: "printers", label: "Printers", icon: MdPrint },
  /* palette + logo for the open business — its own endpoint, not the
     settings document, so it renders outside the load/error states below */
  { id: "branding", label: "Branding", icon: MdPalette },
];

/* The three category lists a product type can be filed under, each its
   own settings field holding a JSON array of type names. */
const CATEGORIES = [
  {
    field: "cc_types",
    label: "Casket cover",
    /* the one rule every screen and count uses — src/productCategories.js */
    help: "goes with the casket cover: its list column and run sheet stop — unless it is added in the Tributes half of the order form. Only the Casket cover type itself is counted as a casket cover",
  },
  { field: "pedestal_types", label: "Pedestal", help: "counted as a pedestal product" },
  { field: "easel_types", label: "Easel", help: "counted as an easel product" },
];

const parseList = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

const parsePrices = (raw) =>
  String(raw || "")
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s !== "" && !isNaN(Number(s)))
    .map((s) => Number(s));

const money = (n) => `$${Number(n).toFixed(2)}`;

const toName = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/* ------------------------------------------------------------------ */

export function SettingsRedesign() {
  const { user } = useAuth() || {};
  const canManage = can(user, "settings:manage");

  const [section, setSection] = useState("fees");
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    axios
      .get(BASE_SERVER_URL + "/settings/getAll")
      .then((doc) => {
        if (doc.data.status !== "success") {
          setLoadError(true);
          setLoading(false);
          return;
        }
        setSettings((doc.data.response || [])[0] || {});
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        setLoadError(true);
      });
  }, []);

  useEffect(load, [load]);

  /* Every write on this page goes through here: one PATCH-shaped POST,
     the server's answer becomes the new state, and the caller is told
     whether it landed. */
  const save = useCallback(
    (patch, what) => {
      if (!canManage) {
        toast.warn("You don't have permission to change settings.");
        return Promise.resolve(false);
      }
      setSaving(true);
      return axios
        .post(BASE_SERVER_URL + "/settings/update", patch)
        .then((doc) => {
          setSaving(false);
          if (doc.data.status !== "success") {
            toast.error(`Couldn't save ${what}.`);
            return false;
          }
          // the endpoint answers with the whole settings document
          setSettings((prev) =>
            doc.data.response && typeof doc.data.response === "object"
              ? doc.data.response
              : { ...prev, ...patch }
          );
          toast.success(`${toName(what)} saved.`);
          return true;
        })
        .catch(() => {
          setSaving(false);
          toast.error(`Couldn't save ${what}.`);
          return false;
        });
    },
    [canManage]
  );

  const types = useMemo(() => parseList(settings.product_types), [settings]);
  const counts = {
    types: types.length,
  };

  const active = SECTIONS.find((s) => s.id === section) || SECTIONS[0];

  return (
    <div className="st-page">
      <nav className="st-nav">
        <div className="st-nav-eyebrow">Settings</div>
        {SECTIONS.map((s) => {
          const Icon = s.icon;
          return (
            <button
              key={s.id}
              type="button"
              className={`st-navitem${s.id === section ? " is-active" : ""}`}
              onClick={() => setSection(s.id)}
            >
              <Icon />
              {s.label}
              {counts[s.id] !== undefined && (
                <span className="st-navitem-n rf-num">{counts[s.id]}</span>
              )}
            </button>
          );
        })}
        <span className="rf-spacer" />
        <p className="st-nav-note">
          {canManage
            ? "Changes take effect on new work orders. Orders already entered keep the values they were priced at."
            : "You can read these settings but not change them."}
        </p>
      </nav>

      <div className="st-body">
        <header className="st-head">
          <div className="st-head-main">
            <h1 className="st-title">{active.label}</h1>
            <p className="st-lede">{LEDE[active.id]}</p>
          </div>
          <span className="rf-spacer" />
          {section !== "printers" && section !== "branding" && (
            <button
              type="button"
              className="rf-btn rf-btn-ghost"
              onClick={load}
              title="Reload settings"
            >
              <MdRefresh style={{ verticalAlign: "-2px" }} /> Reload
            </button>
          )}
        </header>

        {!canManage && (
          <div className="st-readonly">
            <span>
              <b>Read only.</b> Your role can see these settings but not change
              them — ask an owner or admin if something here needs updating.
            </span>
          </div>
        )}

        {section === "branding" ? (
          <BrandingSection canManage={canManage} />
        ) : section === "printers" ? (
          <PrintersSection canManage={canManage} />
        ) : loadError ? (
          <div className="st-card">
            <div className="st-state">
              <b>Couldn&apos;t load settings.</b>
              The server didn&apos;t answer that request.
              <div>
                <button type="button" className="rf-btn rf-btn-ghost" onClick={load}>
                  Try again
                </button>
              </div>
            </div>
          </div>
        ) : loading ? (
          <div className="st-card">
            {Array.from({ length: 5 }).map((_, i) => (
              <div className="st-skel" key={i} />
            ))}
          </div>
        ) : section === "fees" ? (
          <FeesSection settings={settings} save={save} canManage={canManage} saving={saving} />
        ) : section === "types" ? (
          <TypesSection
            settings={settings}
            types={types}
            save={save}
            canManage={canManage}
            saving={saving}
          />
        ) : (
          <FlowersSection canManage={canManage} />
        )}
      </div>
    </div>
  );
}

const LEDE = {
  fees: "The rates and prices the order form starts every new work order with.",
  types:
    "The product types the order form offers, and which of them count as casket covers, pedestals or easels.",
  flowers:
    "The flowers a product's contents can be built from. These names are what the inventory list totals up.",
  printers:
    "Printers this business can reach, and which one label printing goes to.",
  branding:
    "This business's colours and logo. They apply to every page, pop-up and printed sheet.",
};

/* ================= fees & prices ================= */

function FeesSection({ settings, save, canManage, saving }) {
  return (
    <>
      <div className="st-grid-2">
        <div className="st-card">
          <div className="st-card-head">
            <span className="st-card-title">GST</span>
          </div>
          <p className="st-card-help">
            Added to every taxable line on a new order.
          </p>
          <NumberField
            value={settings.gst}
            suffix="%"
            disabled={!canManage}
            saving={saving}
            onSave={(v) => save({ gst: v }, "GST")}
          />
        </div>

        <PriceListCard
          title="Delivery fees"
          help="The delivery charges the order form offers. Add each one separately."
          field="delivery_fee"
          value={settings.delivery_fee}
          save={save}
          canManage={canManage}
          saving={saving}
        />
        <PriceListCard
          title="Sash prices"
          help="The sash prices the order form offers, smallest first."
          field="sash_prices"
          value={settings.sash_prices}
          save={save}
          canManage={canManage}
          saving={saving}
        />
      </div>
    </>
  );
}

/* A single number, saved on Enter or with the button that appears once
   it differs from what the server holds. Escape puts it back. */
function NumberField({ value, suffix, prefix, disabled, saving, onSave }) {
  const server = value === undefined || value === null ? "" : String(value);
  const [draft, setDraft] = useState(server);

  useEffect(() => setDraft(server), [server]);

  const dirty = draft.trim() !== server.trim();
  const valid = draft.trim() !== "" && !isNaN(Number(draft));

  function commit() {
    if (!dirty) return;
    if (!valid) {
      toast.error("That needs to be a number.");
      return;
    }
    onSave(Number(draft));
  }

  return (
    <div className="st-fee-row">
      <div className="st-fee-input">
        {prefix && <span className="st-fee-fix">{prefix}</span>}
        <input
          className="st-fee-value"
          value={draft}
          inputMode="decimal"
          disabled={disabled}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") setDraft(server);
          }}
        />
        {suffix && <span className="st-fee-fix">{suffix}</span>}
      </div>
      {dirty && !disabled && (
        <div className="st-dirty">
          <span className="st-dirty-note">{valid ? "Unsaved" : "Not a number"}</span>
          <button
            type="button"
            className="rf-btn rf-btn-ghost st-btn-sm"
            onClick={() => setDraft(server)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rf-btn rf-btn-primary st-btn-sm"
            onClick={commit}
            disabled={!valid || saving}
          >
            Save
          </button>
        </div>
      )}
    </div>
  );
}

/* delivery_fee / sash_prices: stored as one comma-separated string, shown
   and edited as individual prices. */
function PriceListCard({ title, help, field, value, save, canManage, saving }) {
  const prices = useMemo(() => parsePrices(value), [value]);
  const [draft, setDraft] = useState("");

  function write(next) {
    return save({ [field]: next.join(",") }, title.toLowerCase());
  }

  function add(e) {
    e.preventDefault();
    const n = Number(draft.trim());
    if (draft.trim() === "" || isNaN(n)) {
      toast.error("Enter a price as a number, like 12.50.");
      return;
    }
    if (prices.indexOf(n) !== -1) {
      toast.info(`${money(n)} is already on the list.`);
      return;
    }
    write([...prices, n].sort((a, b) => a - b)).then((ok) => ok && setDraft(""));
  }

  return (
    <div className="st-card">
      <div className="st-card-head">
        <span className="st-card-title">{title}</span>
        <span className="st-card-meta">
          {prices.length} {prices.length === 1 ? "price" : "prices"}
        </span>
      </div>
      <p className="st-card-help">{help}</p>

      <div className="st-prices">
        {prices.length === 0 && (
          <span className="st-card-help" style={{ margin: 0 }}>
            None set.
          </span>
        )}
        {prices.map((p) => (
          <span
            key={p}
            className={`st-price${canManage ? "" : " is-readonly"}`}
          >
            {money(p)}
            {canManage && (
              <button
                type="button"
                className="st-x"
                title={`Remove ${money(p)}`}
                disabled={saving}
                onClick={() => write(prices.filter((x) => x !== p))}
              >
                ×
              </button>
            )}
          </span>
        ))}
      </div>

      {canManage && (
        <form className="st-add" onSubmit={add}>
          <input
            className="st-input is-narrow"
            value={draft}
            inputMode="decimal"
            placeholder="e.g. 12.50"
            onChange={(e) => setDraft(e.target.value)}
          />
          <button type="submit" className="rf-btn rf-btn-primary" disabled={saving}>
            <MdAdd style={{ verticalAlign: "-2px" }} /> Add price
          </button>
        </form>
      )}
    </div>
  );
}

/* ================= product types ================= */

function TypesSection({ settings, types, save, canManage, saving }) {
  const [draft, setDraft] = useState("");
  const [confirming, setConfirming] = useState(null);

  const membership = useMemo(() => {
    const map = {};
    CATEGORIES.forEach((c) => {
      map[c.field] = parseList(settings[c.field]);
    });
    return map;
  }, [settings]);

  const sorted = useMemo(() => [...types].sort((a, b) => a.localeCompare(b)), [types]);

  function addType(e) {
    e.preventDefault();
    const name = toName(draft.trim());
    if (!name) return;
    if (types.some((t) => t.toLowerCase() === name.toLowerCase())) {
      toast.info(`“${name}” is already a product type.`);
      return;
    }
    save({ product_types: JSON.stringify([...types, name]) }, "product type").then(
      (ok) => ok && setDraft("")
    );
  }

  /* Removing a type also drops it from every category it was filed
     under — the legacy screen left those entries behind, so a category
     could name a type that no longer existed. */
  function removeType(name) {
    const patch = { product_types: JSON.stringify(types.filter((t) => t !== name)) };
    CATEGORIES.forEach((c) => {
      if (membership[c.field].indexOf(name) !== -1) {
        patch[c.field] = JSON.stringify(
          membership[c.field].filter((t) => t !== name)
        );
      }
    });
    save(patch, "product type").then((ok) => ok && setConfirming(null));
  }

  function toggleCategory(field, name) {
    const current = membership[field];
    const next =
      current.indexOf(name) === -1
        ? [...current, name]
        : current.filter((t) => t !== name);
    save({ [field]: JSON.stringify(next) }, "categories");
  }

  return (
    <div className="st-card">
      <div className="st-card-head">
        <span className="st-card-title">Product types</span>
        <span className="st-card-meta">
          {types.length} {types.length === 1 ? "type" : "types"}
        </span>
      </div>
      <p className="st-card-help">
        Each type can also be filed under one or more categories:{" "}
        {CATEGORIES.map((c, i) => (
          <span key={c.field}>
            {i > 0 && (i === CATEGORIES.length - 1 ? " and " : ", ")}
            <b>{c.label}</b> — {c.help}
          </span>
        ))}
        .
      </p>

      {sorted.length === 0 ? (
        <div className="st-state">
          <b>No product types yet.</b>
          The order form has nothing to offer until one is added.
        </div>
      ) : (
        <div className="st-rows">
          {sorted.map((name) => {
            const isConfirming = confirming === name;
            const filed = CATEGORIES.filter(
              (c) => membership[c.field].indexOf(name) !== -1
            );
            return (
              <div
                className={`st-row${isConfirming ? " is-confirming" : ""}`}
                key={name}
              >
                <span className="st-row-name">
                  {name}
                  <small>
                    {filed.length === 0
                      ? "Uncategorised"
                      : filed.map((c) => c.label).join(" · ")}
                  </small>
                </span>

                {isConfirming ? (
                  <div className="st-confirm">
                    <span className="st-confirm-t">Remove “{name}”?</span>
                    <button
                      type="button"
                      className="rf-btn rf-btn-ghost st-btn-sm"
                      onClick={() => setConfirming(null)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="rf-btn st-btn-danger st-btn-sm"
                      onClick={() => removeType(name)}
                      disabled={saving}
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="st-flags">
                      {CATEGORIES.map((c) => {
                        const on = membership[c.field].indexOf(name) !== -1;
                        return (
                          <button
                            key={c.field}
                            type="button"
                            className={`st-flag${on ? " is-on" : ""}`}
                            disabled={!canManage || saving}
                            title={
                              canManage
                                ? `${on ? "Remove from" : "Add to"} ${c.label}`
                                : `${c.label}: ${on ? "yes" : "no"}`
                            }
                            onClick={() => toggleCategory(c.field, name)}
                          >
                            {c.label}
                          </button>
                        );
                      })}
                    </div>
                    <div className="st-row-acts">
                      <button
                        type="button"
                        className="st-mini is-danger"
                        title="Remove this product type"
                        disabled={!canManage || saving}
                        onClick={() => setConfirming(name)}
                      >
                        <MdDelete />
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {canManage && (
        <form className="st-add" onSubmit={addType}>
          <input
            className="st-input"
            value={draft}
            placeholder="Add a product type, e.g. Casket spray"
            onChange={(e) => setDraft(e.target.value)}
          />
          <button type="submit" className="rf-btn rf-btn-primary" disabled={saving}>
            <MdAdd style={{ verticalAlign: "-2px" }} /> Add type
          </button>
        </form>
      )}
    </div>
  );
}

/* ================= content flowers ================= */

function FlowersSection({ canManage }) {
  const [flowers, setFlowers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState(null); // { _id, flower_name }
  const [confirming, setConfirming] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    axios
      .get(BASE_SERVER_URL + "/api/flowers/get-all")
      .then((res) => {
        if (!res.data.success) {
          setLoadError(true);
          setLoading(false);
          return;
        }
        setFlowers(res.data.data || []);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        setLoadError(true);
      });
  }, []);

  useEffect(load, [load]);

  const sorted = useMemo(
    () =>
      [...flowers].sort((a, b) =>
        String(a.flower_name || "").localeCompare(String(b.flower_name || ""))
      ),
    [flowers]
  );

  function add(e) {
    e.preventDefault();
    const name = draft.trim();
    if (!name) return;
    if (flowers.some((f) => String(f.flower_name || "").toLowerCase() === name.toLowerCase())) {
      toast.info(`“${name}” is already on the list.`);
      return;
    }
    setBusy(true);
    axios
      .post(BASE_SERVER_URL + "/api/flowers/add-new", { flower_name: name })
      .then((res) => {
        setBusy(false);
        if (!res.data.success) {
          toast.error("Couldn't add that flower.");
          return;
        }
        setDraft("");
        toast.success(`${name} added.`);
        load();
      })
      .catch(() => {
        setBusy(false);
        toast.error("Couldn't add that flower.");
      });
  }

  function rename() {
    const name = String(editing.flower_name || "").trim();
    if (!name) return;
    setBusy(true);
    axios
      .put(BASE_SERVER_URL + "/api/flowers/update/" + editing._id, {
        ...editing,
        flower_name: name,
      })
      .then((res) => {
        setBusy(false);
        if (!res.data.success) {
          toast.error("Couldn't rename that flower.");
          return;
        }
        setEditing(null);
        toast.success("Flower renamed.");
        load();
      })
      .catch(() => {
        setBusy(false);
        toast.error("Couldn't rename that flower.");
      });
  }

  function remove(flower) {
    setBusy(true);
    axios
      .delete(BASE_SERVER_URL + "/api/flowers/delete/" + flower._id)
      .then((res) => {
        setBusy(false);
        if (!res.data.success) {
          toast.error("Couldn't remove that flower.");
          return;
        }
        setConfirming(null);
        toast.success(`${flower.flower_name} removed.`);
        load();
      })
      .catch(() => {
        setBusy(false);
        toast.error("Couldn't remove that flower.");
      });
  }

  return (
    <div className="st-card">
      <div className="st-card-head">
        <span className="st-card-title">Content flowers</span>
        <span className="st-card-meta">
          {loading ? "…" : `${flowers.length} ${flowers.length === 1 ? "flower" : "flowers"}`}
        </span>
        <span className="rf-spacer" />
        <button
          type="button"
          className="rf-btn rf-btn-ghost st-btn-sm"
          onClick={load}
          title="Reload"
        >
          <MdRefresh style={{ verticalAlign: "-2px" }} />
        </button>
      </div>
      <p className="st-card-help">
        A product&apos;s contents are built from these, and the inventory list
        totals stems by these names. Renaming one here renames it everywhere it
        is referenced by id.
      </p>

      {loading ? (
        Array.from({ length: 4 }).map((_, i) => <div className="st-skel" key={i} />)
      ) : loadError ? (
        <div className="st-state">
          <b>Couldn&apos;t load the flower list.</b>
          The server didn&apos;t answer that request.
          <div>
            <button type="button" className="rf-btn rf-btn-ghost" onClick={load}>
              Try again
            </button>
          </div>
        </div>
      ) : sorted.length === 0 ? (
        <div className="st-state">
          <b>No flowers yet.</b>
          Add the flowers your products are made from.
        </div>
      ) : (
        <div className="st-rows">
          {sorted.map((flower) => {
            const isEditing = editing && editing._id === flower._id;
            const isConfirming = confirming === flower._id;
            return (
              <div
                className={`st-row${isConfirming ? " is-confirming" : ""}`}
                key={flower._id}
              >
                {isEditing ? (
                  <>
                    <input
                      className="st-input"
                      value={editing.flower_name}
                      autoFocus
                      onChange={(e) =>
                        setEditing({ ...editing, flower_name: e.target.value })
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") rename();
                        if (e.key === "Escape") setEditing(null);
                      }}
                    />
                    <div className="st-row-acts">
                      <button
                        type="button"
                        className="st-mini"
                        title="Save"
                        disabled={busy}
                        onClick={rename}
                      >
                        <MdCheck />
                      </button>
                      <button
                        type="button"
                        className="st-mini"
                        title="Cancel"
                        onClick={() => setEditing(null)}
                      >
                        <MdClose />
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <span className="st-row-name">{flower.flower_name}</span>
                    {isConfirming ? (
                      <div className="st-confirm">
                        <span className="st-confirm-t">
                          Remove “{flower.flower_name}”?
                        </span>
                        <button
                          type="button"
                          className="rf-btn rf-btn-ghost st-btn-sm"
                          onClick={() => setConfirming(null)}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="rf-btn st-btn-danger st-btn-sm"
                          onClick={() => remove(flower)}
                          disabled={busy}
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div className="st-row-acts">
                        <button
                          type="button"
                          className="st-mini"
                          title="Rename this flower"
                          disabled={!canManage || busy}
                          onClick={() => setEditing({ ...flower })}
                        >
                          <MdEditNote />
                        </button>
                        <button
                          type="button"
                          className="st-mini is-danger"
                          title="Remove this flower"
                          disabled={!canManage || busy}
                          onClick={() => setConfirming(flower._id)}
                        >
                          <MdDelete />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {canManage && !loadError && (
        <form className="st-add" onSubmit={add}>
          <input
            className="st-input"
            value={draft}
            placeholder="Add a flower, e.g. White chrysanthemum"
            onChange={(e) => setDraft(e.target.value)}
          />
          <button type="submit" className="rf-btn rf-btn-primary" disabled={busy}>
            <MdAdd style={{ verticalAlign: "-2px" }} /> Add flower
          </button>
        </form>
      )}
    </div>
  );
}

/* ================= printers ================= */

/* The legacy widget opened a confirm dialog whose body was a single
   toggle offering the one task there is, which could be un-toggled into
   a state where Save did nothing. Here the row says what it is and a
   button sets it. */
function PrintersSection({ canManage }) {
  const [printers, setPrinters] = useState([]);
  const [defaults, setDefaults] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);

  const loadDefaults = useCallback(
    () =>
      axios
        .get(BASE_SERVER_URL + "/api/printers/defaults")
        .then((res) => {
          if (res.data.success) setDefaults(res.data.defaults || {});
        })
        .catch(() => {}),
    []
  );

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    Promise.all([
      axios.get(BASE_SERVER_URL + "/api/printers"),
      loadDefaults(),
    ])
      .then(([res]) => {
        if (!res.data.success) {
          setLoadError(true);
          setLoading(false);
          return;
        }
        setPrinters(res.data.printers || []);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        setLoadError(true);
      });
  }, [loadDefaults]);

  useEffect(load, [load]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return printers;
    return printers.filter(
      (p) =>
        String(p.name || "").toLowerCase().indexOf(q) !== -1 ||
        String((p.computer || {}).name || "").toLowerCase().indexOf(q) !== -1
    );
  }, [printers, search]);

  function setLabelPrinter(printer) {
    if (!canManage) {
      toast.warn("You don't have permission to change settings.");
      return;
    }
    setBusyId(printer.id);
    axios
      .post(BASE_SERVER_URL + "/api/printers/defaults", {
        task: "label_print",
        printerId: printer.id,
      })
      .then((res) => {
        setBusyId(null);
        if (!res.data.success) {
          toast.error(res.data.message || "Couldn't set that printer.");
          return;
        }
        toast.success(`${printer.name} is now the label printer.`);
        loadDefaults();
      })
      .catch(() => {
        setBusyId(null);
        toast.error("Couldn't set that printer.");
      });
  }

  const currentId = (defaults.label_print || {}).printerId;

  return (
    <div className="st-card">
      <div className="st-card-head">
        <span className="st-card-title">Label printing</span>
        <span className="st-card-meta">
          {loading
            ? "…"
            : `${printers.length} ${printers.length === 1 ? "printer" : "printers"}`}
        </span>
        <span className="rf-spacer" />
        <button
          type="button"
          className="rf-btn rf-btn-ghost st-btn-sm"
          onClick={load}
          title="Reload"
        >
          <MdRefresh style={{ verticalAlign: "-2px" }} />
        </button>
      </div>
      <p className="st-card-help">
        Labels print to one printer for the whole business. Pick it here; every
        label job goes to it until it is changed.
      </p>

      {!loading && printers.length > 3 && (
        <div className="st-add" style={{ marginTop: 0, marginBottom: 13 }}>
          <input
            className="st-input"
            value={search}
            placeholder="Find a printer by name or computer"
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      )}

      {loading ? (
        Array.from({ length: 3 }).map((_, i) => (
          <div className="st-skel" style={{ height: 52 }} key={i} />
        ))
      ) : loadError ? (
        <div className="st-state">
          <b>Couldn&apos;t reach the print service.</b>
          No printers could be listed.
          <div>
            <button type="button" className="rf-btn rf-btn-ghost" onClick={load}>
              Try again
            </button>
          </div>
        </div>
      ) : shown.length === 0 ? (
        <div className="st-state">
          <b>
            {printers.length === 0
              ? "No printers found."
              : `No printer matches “${search.trim()}”.`}
          </b>
          {printers.length === 0
            ? "A printer shows up here once its computer is connected to the print service."
            : "Clear the search to see them all."}
        </div>
      ) : (
        <div className="st-rows">
          {shown.map((printer) => {
            const isDefault = currentId === printer.id;
            const online = String(printer.state || "").toLowerCase() === "online";
            return (
              <div className="st-printer" key={printer.id}>
                <span className="st-printer-ic">
                  <MdPrint />
                </span>
                <div className="st-printer-main">
                  <div className="st-printer-name">{printer.name}</div>
                  <div className="st-printer-sub">
                    <span>{(printer.computer || {}).name || "Unknown computer"}</span>
                    <span>·</span>
                    <span
                      className={`st-dot${online ? " is-on" : " is-off"}`}
                      aria-hidden="true"
                    />
                    <span style={{ textTransform: "capitalize" }}>
                      {printer.state || "unknown"}
                    </span>
                  </div>
                </div>
                {isDefault ? (
                  <span className="st-badge">Label printer</span>
                ) : (
                  canManage && (
                    <button
                      type="button"
                      className="rf-btn rf-btn-ghost st-btn-sm"
                      onClick={() => setLabelPrinter(printer)}
                      disabled={busyId !== null}
                    >
                      {busyId === printer.id ? "Setting…" : "Use for labels"}
                    </button>
                  )
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default SettingsRedesign;
